/**
 * K-T-6 — Layer-3 hook for the canonical technician summary tuple.
 *
 * Thin wrapper around `kpi_technician_summary(shop_id, tech_id,
 * start_at, end_at)`. Holds NO local KPI math — the RPC is the source
 * of truth. See
 * `supabase/migrations/20260430150000_kpi_technician_summary.sql`
 * for the canonical formulas (efficiency delegated to
 * `kpi_technician_efficiency`; comeback / diagnostic each gated at
 * sample_size < 3; hours / jobs_completed never gated).
 *
 * Replaces (in `TechnicianPerformanceCard`):
 *   - the local tickets useQuery
 *   - the local ticket_jobs useQuery
 *   - the standalone `useTechnicianEfficiency` call
 *
 * After K-T-6 the card issues exactly two server round-trips: the
 * employment-history lookup that resolves `tech_id` from
 * `technicianProfileId`, and this composite summary RPC.
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { TechnicianSummaryResult } from '../../../supabase/functions/_shared/kpiCanonical';

export interface UseTechnicianSummaryOptions {
  shopId: string;
  techId: string;
  /** ISO timestamp. Inclusive lower bound of the half-open window. */
  startAt: string;
  /** ISO timestamp. Exclusive upper bound. */
  endAt: string;
}

export interface UseTechnicianSummaryResult {
  data: TechnicianSummaryResult | null;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
}

/**
 * Returns the canonical technician summary tuple.
 *
 * Stable query key:
 *   ['kpi', 'kpi_technician_summary', shopId, techId, startAt, endAt]
 */
export function useTechnicianSummary(
  opts: UseTechnicianSummaryOptions,
): UseTechnicianSummaryResult {
  const query = useQuery({
    queryKey: [
      'kpi',
      'kpi_technician_summary',
      opts.shopId,
      opts.techId,
      opts.startAt,
      opts.endAt,
    ],
    queryFn: async (): Promise<TechnicianSummaryResult | null> => {
      // Cast through unknown until supabase types.ts codegen catches up
      // to the new RPC. The return shape is pinned by the migration's
      // RETURNS TABLE clause and the `TechnicianSummaryResult` interface.
      const { data, error } = await (supabase.rpc as unknown as (
        fn: string,
        args: {
          p_shop_id: string;
          p_tech_id: string;
          p_start_at: string;
          p_end_at: string;
        },
      ) => Promise<{
        data: TechnicianSummaryResult[] | null;
        error: unknown;
      }>)('kpi_technician_summary', {
        p_shop_id: opts.shopId,
        p_tech_id: opts.techId,
        p_start_at: opts.startAt,
        p_end_at: opts.endAt,
      });

      if (error) throw error;
      return Array.isArray(data) && data.length > 0 ? data[0] : null;
    },
    enabled: Boolean(
      opts.shopId && opts.techId && opts.startAt && opts.endAt,
    ),
    staleTime: 5 * 60 * 1000,
  });

  return {
    data: query.data ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
  };
}
